/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
// Extends From
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import { assert } from '@ember/debug';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
// Internal Imports
import DevicePlatform from '@linc-technologies/linced-common/enums/device-platform';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { classNames } from '@ember-decorators/component';

@classNames('user-device')
export default class UserDevice extends Component {
  @service
  session;

  @service
  permissions;

  @service
  toaster;

  // Attrs
  // device is a device that may or may not receive notifications.
  // @required
  device = null;

  // currentDeviceId provides the identifier of the current mobile device in
  // use.
  // @optional
  @tracked currentDeviceId = '';

  // Methods
  // canEdit specifies whether the current user is allowed to manage the given
  // device.
  @computed('session.person.id', 'device.personId')
  get canEdit() {
    if (this.session.person.id === this.device.personId) {
      return true;
    }

    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  // deviceType will return either iOS or Android
  @computed('device.platform')
  get deviceType() {
    const { platform } = this.device;
    if (!platform || platform === DevicePlatform.UNSPECIFIED) {
      return '';
    }

    return DevicePlatform.getLabel(platform);
  }

  // deviceImageUri returns an image URI for the provided device based on the
  // device's platform (iOS or Android).
  @computed('deviceType')
  get deviceImageUri() {
    if (!this.deviceType) {
      return;
    }

    return `/assets/images/devices/${this.deviceType.toLowerCase()}.png`;
  }

  // isCurrentDevice checks to see if the provided device is the device the
  // user is currently on.
  @computed('currentDeviceId', 'device.physicalId')
  get isCurrentDevice() {
    if (!this.currentDeviceId || !this.device.physicalId) {
      return false;
    }

    // extract IDs for comparison
    let currentDeviceId = this.currentDeviceId.toLowerCase();
    let deviceId = this.device.physicalId.toLowerCase();

    return currentDeviceId === deviceId;
  }

  // deleteDevice removes the device from the user's list.
  @action
  deleteDevice() {
    const { make, model } = this.device;
    let deviceName = `${make} ${model}`;
    this.device
      .destroyRecord()
      .then(() =>
        this.toaster.success(`Device '${deviceName}' successfully removed!`),
      )
      .catch(() =>
        this.toaster.error(
          `An error occurred attempting to delete '${deviceName}'. Please try again.`,
        ),
      );
  }

  // Lifecycle hooks
  didReceiveAttrs() {
    assert(
      'component: user-device must be given a device record',
      this.device !== null,
    );
    super.didReceiveAttrs(...arguments);
  }
}
